// src/services/messaging/SequentialMessageFlow.ts

/**
 * Options for configuring the sequential message flow
 */
export interface SequentialMessageOptions {
  /** Milliseconds to wait between messages (default: 2000) */
  messagePause?: number;
  /** Milliseconds between streaming chunks (default: 30) */
  streamingInterval?: number;
  /** Characters per chunk for streaming simulation (default: 3) */
  streamingSpeed?: number;
  /** API endpoint for message generation (default: '/api/generate-message') */
  apiEndpoint?: string;
  /** Called when a message begins generating */
  onMessageStart?: (index: number, context: string) => void;
  /** Called when a streaming chunk is received */
  onChunkReceived?: (index: number, chunk: string) => void;
  /** Called when a message is fully generated */
  onMessageComplete?: (index: number, message: string) => void;
  /** Called when the entire sequence is complete */
  onSequenceComplete?: () => void;
}

/**
 * Type definition for a message in the sequence
 */
export interface MessageContext {
  /** Context/prompt for the LLM to generate a response from */
  context: string;
  /** Optional metadata to associate with this message */
  metadata?: any;
}

/**
 * A utility for generating and streaming a sequence of LLM-generated messages
 */
export class SequentialMessageFlow {
  private messages: MessageContext[];
  private currentIndex: number = 0;
  private isActive: boolean = false;
  private options: Required<SequentialMessageOptions>;
  private conversationHistory: string[] = []; // Track what Trippy has said

  
  /**
   * Create a new sequential message flow
   * 
   * @param messages Array of message contexts to process
   * @param options Configuration options
   */
  constructor(messages: (MessageContext | string)[], options: SequentialMessageOptions = {}) {
    // Convert string contexts to MessageContext objects
    this.messages = messages.map(msg => 
      typeof msg === 'string' ? { context: msg } : msg
    );
    
    // Set options with defaults
    this.options = {
      messagePause: 1500,
      streamingInterval: 80,
      streamingSpeed: 15,
      apiEndpoint: '/api/generate-message',
      onMessageStart: () => {},
      onChunkReceived: () => {},
      onMessageComplete: () => {},
      onSequenceComplete: () => {},
      ...options
    };
  }
  
  /**
   * Start the message sequence
   */
  async start(): Promise<void> {
    this.isActive = true;
    await this.processNextMessage();
  }
  
  /**
   * Process the next message in the sequence
   */
  private async processNextMessage(): Promise<void> {
    if (!this.isActive || this.currentIndex >= this.messages.length) {
      this.complete();
      return;
    }
    
    const messageContext = this.messages[this.currentIndex];
    
    // Notify that message generation is starting
    this.options.onMessageStart(this.currentIndex, messageContext.context);
    
    try {
      // Generate the message with streaming
      const message = await this.generateStreamingMessage(
        messageContext.context,
        (chunk) => this.options.onChunkReceived(this.currentIndex, chunk)
      );
      
      // Notify that message is complete
      this.options.onMessageComplete(this.currentIndex, message);
      
      // Move to next message
      this.currentIndex++;
      
      // Schedule next message if there are more
      if (this.currentIndex < this.messages.length && this.isActive) {
        setTimeout(() => {
          this.processNextMessage();
        }, this.options.messagePause);
      } else {
        this.complete();
      }
    } catch (error) {
      console.error('Error generating message:', error);
      // Move to next message despite error
      this.currentIndex++;
      setTimeout(() => {
        this.processNextMessage();
      }, 500); // Shorter delay after error
    }
  }
  
  /**
   * Generate a message from the LLM API
   */

  private async generateMessage(context: string): Promise<string> {
    try {
      // Build conversation context like builder.ts does
      const conversationContext = this.conversationHistory.length > 0 
        ? `Previous messages in this conversation:\n${this.conversationHistory.map((msg, i) => `${i + 1}. ${msg}`).join('\n')}\n\n`
        : '';
      
      const response = await fetch(this.options.apiEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify({ 
          context,
          conversationHistory: conversationContext // Send history to API
        })
      });
      
      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.success === false) {
        throw new Error(data.message || 'API reported failure');
      }
      
      // Store the generated message in history
      if (data.message) {
        this.conversationHistory.push(data.message);
      }
      
      return data.message || "Failed to generate message";
    } catch (error) {
      console.error('API call failed:', error);
      return "I couldn't generate a response right now.";
    }
  }
  
  /**
  * Generate a message with simulated streaming
  */

  private async generateStreamingMessage(
    context: string,
    onChunk: (chunk: string) => void
  ): Promise<string> {
    try {
      const conversationContext = this.conversationHistory.length > 0 
        ? `Previous messages in this conversation:\n${this.conversationHistory.map((msg, i) => `${i + 1}. ${msg}`).join('\n')}\n\n`
        : '';
      
      const response = await fetch(this.options.apiEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify({ 
          context,
          conversationHistory: conversationContext
        })
      });

      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }

      const reader = response.body?.getReader();
      if (!reader) throw new Error('No response body');
      
      const decoder = new TextDecoder();
      let fullMessage = '';
      let buffer = '';
      
      // Process streaming response directly
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';
        
        for (const line of lines) {
          if (line.trim() === '') continue;
          if (line.startsWith('data: ')) {
            const data = line.slice(6).trim();
            if (data === '[DONE]') continue;
            
            try {
              const parsed = JSON.parse(data);
              if (parsed.text) {
                // Direct streaming: immediately pass text chunks to UI
                fullMessage += parsed.text;
                onChunk(parsed.text);
              }
            } catch (e) {
              console.error('Failed to parse SSE:', e, data);
            }
          }
        }
      }
      
      // Process any remaining buffer content
      if (buffer) {
        try {
          if (buffer.startsWith('data: ')) {
            const data = buffer.slice(6).trim();
            if (data !== '[DONE]') {
              const parsed = JSON.parse(data);
              if (parsed.text) {
                fullMessage += parsed.text;
                onChunk(parsed.text);
              }
            }
          }
        } catch (e) {
          console.error('Failed to parse remaining buffer:', e);
        }
      }

      if (fullMessage) {
        this.conversationHistory.push(fullMessage);
      }
      
      return fullMessage;
      
    } catch (error) {
      console.error('Streaming failed:', error);
      return this.generateMessage(context);
    }
  }
    /* try {
      const conversationContext = this.conversationHistory.length > 0 
        ? `Previous messages in this conversation:\n${this.conversationHistory.map((msg, i) => `${i + 1}. ${msg}`).join('\n')}\n\n`
        : '';
      
      const response = await fetch(this.options.apiEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify({ 
          context,
          conversationHistory: conversationContext
        })
      });

      if (!response.ok) throw new Error(`API error: ${response.status}`);
      
      const reader = response.body?.getReader();
      if (!reader) throw new Error('No response body');
      
      const decoder = new TextDecoder();
      let fullMessage = '';
      let buffer = '';
      
      // Process streaming response directly
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';
        
        for (const line of lines) {
          if (line.trim() === '') continue;
          if (line.startsWith('data: ')) {
            const data = line.slice(6).trim();
            if (data === '[DONE]') continue;
            
            try {
              const parsed = JSON.parse(data);
              if (parsed.text) {
                fullMessage += parsed.text;
                // Forward chunk directly to UI without buffering
                onChunk(parsed.text);
              }
            } catch (e) {
              console.error('Failed to parse SSE:', e, data);
            }
          }
        }
      }
      
      if (fullMessage) {
        this.conversationHistory.push(fullMessage);
      }
      
      return fullMessage;
    } catch (error) {
      console.error('Streaming failed:', error);
      return this.generateMessage(context);
    } 
  }*/

/*   private async generateStreamingMessage_UNUSED(
    context: string,
    onChunk: (chunk: string) => void
  ): Promise<string> {
    try {
      const conversationContext = this.conversationHistory.length > 0 
        ? `Previous messages in this conversation:\n${this.conversationHistory.map((msg, i) => `${i + 1}. ${msg}`).join('\n')}\n\n`
        : '';
      
      const response = await fetch(this.options.apiEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify({ 
          context,
          conversationHistory: conversationContext
        })
      });

      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }

      const reader = response.body?.getReader();
      if (!reader) throw new Error('No response body');
      
      const decoder = new TextDecoder();
      let fullMessage = '';
      let buffer = '';
      let characterBuffer = ''; // Buffer to accumulate characters
      
      // Process characters at controlled speed
      const processCharacters = () => {
        if (characterBuffer.length > 0) {
          const charsToSend = Math.min(this.options.streamingSpeed, characterBuffer.length);
          const chunk = characterBuffer.substring(0, charsToSend);
          characterBuffer = characterBuffer.substring(charsToSend);
          onChunk(chunk);
        }
      };
      
      const interval = setInterval(processCharacters, this.options.streamingInterval);

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';
        
        for (const line of lines) {
          if (line.trim() === '') continue;
          if (line.startsWith('data: ')) {
            const data = line.slice(6).trim();
            if (data === '[DONE]') continue;
            
            try {
              const parsed = JSON.parse(data);
              if (parsed.text) {
                fullMessage += parsed.text;
                // Add to character buffer instead of sending immediately
                characterBuffer += parsed.text;
              }
            } catch (e) {
              console.error('Failed to parse SSE:', e, data);
            }
          }
        }
      }
      
      // Process remaining characters
      while (characterBuffer.length > 0) {
        await new Promise(resolve => setTimeout(resolve, this.options.streamingInterval));
        processCharacters();
      }
      
      clearInterval(interval);

      if (fullMessage) {
        this.conversationHistory.push(fullMessage);
      }
      
      return fullMessage;
      
    } catch (error) {
      console.error('Streaming failed:', error);
      return this.generateMessage(context);
    }
  } */
  
  /**
   * Mark the sequence as complete
   */
  private complete(): void {
    this.isActive = false;
    this.options.onSequenceComplete();
  }
  
  /**
   * Pause the message sequence
   */
  pause(): void {
    this.isActive = false;
  }
  
  /**
   * Resume the message sequence
   */
  resume(): void {
    if (!this.isActive && !this.isComplete()) {
      this.isActive = true;
      this.processNextMessage();
    }
  }
  
  /**
   * Reset the sequence to the beginning
   */
  reset(): void {
    this.currentIndex = 0;
    this.isActive = false;
    this.conversationHistory = []; // Clear history on reset

  }
  
  /**
   * Check if the sequence is complete
   */
  isComplete(): boolean {
    return this.currentIndex >= this.messages.length;
  }
  
  /**
   * Get the current progress through the sequence
   */
  getProgress(): { current: number, total: number } {
    return {
      current: this.currentIndex,
      total: this.messages.length
    };
  }
}

/**
 * Example usage:
 * 
 * // Define message contexts
 * const onboardingMessages = [
 *   "Hey! I'm Trippy — your personal travel buddy. Think of me as your planner, idea generator, and adventure co-pilot ✈️",
 *   "I can help you plan trips, build day-by-day itineraries, suggest places to eat, stay, and explore — all based on *you*. Whether you know where you're going or just want to dream a little, I've got you.",
 *   "Any any given point, just type \"help\" and I'll pull up a few ideas to get you started. Quick and easy.",
 *   "So... want to start a new trip, explore destinations, or just chat about travel ideas?"
 * ];
 * 
 * // Create the flow
 * const onboardingFlow = new SequentialMessageFlow(onboardingMessages, {
 *   messagePause: 1500, // 1.5 seconds between messages
 *   onMessageStart: (index) => {
 *     // Create empty message in UI
 *     const emptyMessage = { id: Date.now(), content: '', sender: 'bot' };
 *     setMessages(prev => [...prev, emptyMessage]);
 *     setCurrentStreamingMessage(emptyMessage);
 *   },
 *   onChunkReceived: (index, chunk) => {
 *     // Update streaming message in UI
 *     setCurrentStreamingMessage(prev => {
 *       if (!prev) return prev;
 *       return {...prev, content: prev.content + chunk};
 *     });
 *   },
 *   onMessageComplete: (index, message) => {
 *     // Update complete message in UI
 *     setCurrentStreamingMessage(null);
 *   },
 *   onSequenceComplete: () => {
 *     // Mark onboarding as complete
 *     setOnboardingActive(false);
 *   }
 * });
 * 
 * // Start the flow
 * onboardingFlow.start();
 */